<script setup>
import { computed, inject, onMounted, useTemplateRef, ref } from 'vue';
import { useCustomerStore } from './store/customer';
import { isNumeric, formatMoneyValue, statesOptions, capitalizeFirstLetter } from './functions';
import CountdownTimer from '@/js/CountdownTimer.vue';
import VueSelect from "vue3-select-component";
import { vMaska } from "maska/vue";

import SvgNote from '@svg/note.svg?raw';
import SvgVisa from '@svg/visa.svg?raw';
import SvgMastercard from '@svg/mastercard.svg?raw';
import SvgDiners from '@svg/diners.svg?raw';
import SvgAmex from '@svg/amex.svg?raw';
import SvgDiscover from '@svg/discover.svg?raw';
import SvgJcb from '@svg/jcb.svg?raw';

const customerStore = useCustomerStore();
const appConfig = inject('appConfig');
const merchant = appConfig?.merchant;

const cvvMask = computed(() => (customerStore.cardType === 'amex' ? '####' : '###'));

const currentCardTypeSVG = computed(() => {
  switch (customerStore.cardType) {
    case 'visa':
      return SvgVisa;
      break;
    case 'mastercard':
      return SvgMastercard;
      break;
    case 'diners':
      return SvgDiners;
      break;
    case 'amex':
      return SvgAmex;
      break;
    case 'discover':
      return SvgDiscover;
      break;
    case 'jcb':
      return SvgJcb;
      break;
    default:
      return '';
      break;
  }
});

// for loader animation
const checkoutBodyElement = useTemplateRef('checkoutBodyElement');
const timerRef = ref(null);

// for submitting animation, mainly
const submitBtn = useTemplateRef('submit-btn');

const errorsContainer = useTemplateRef('errors-container');
const successContainer = useTemplateRef('success-container');

let btnText = computed(() => {
  if (isNumeric(customerStore.amount) && customerStore.amount > 0) {
    let val = formatMoneyValue(customerStore.amount);
    return `Pay ${val}`;
  } else {
    return 'Amount is empty';
  }
});

async function tokenizeCreditCard() {
  try {
    const baseUrl = merchant?.settings?.is_staging
      ? "https://securelink-staging.valorpaytech.com:4430"
      : "https://securelink.valorpaytech.com:4430";
    const tokenData = {
      // Client token can be generated from Create Page Token
      client_token: customerStore.clientToken,
      // EPI is an end point identifier, Identify the device on valor infrastructure, it's a 10 digit number starts with 2
      epi: merchant?.settings?.epi,
      // Transaction type always should be set as 'cardToken'
      txn_type: "cardToken",
      // Customer card number 15 to 19 digits
      pan: customerStore.ccnumber.replace(/\s/g, ""),
      // Card expiry date, Accepted expiry MMYY & MM/YY (The expiration date must be exactly 4 characters long, consisting of a 2-digit month (with a leading zero if necessary) followed by a 2-digit year (e.g., "0526"))
      expirydate: customerStore.ccexp.replace(" / ", ""),
    }
    const queryString = new URLSearchParams(tokenData).toString()
    const response = await fetch(`${baseUrl}?${queryString}`, {
      method: "POST",
      headers: { "Accept": "*/*", "Content-Type": "application/x-www-form-urlencoded" },
    })

    const result = await response.json();
    // console.log(result);
    if (!response.ok) {
      let message = `HTTP error (${response.status})`;
      if (result?.msg && result?.desc) {
        message = capitalizeFirstLetter(`${result?.msg} (${result?.desc})`);
      } else if (result?.msg) {
        message = capitalizeFirstLetter(`${result?.msg}`);
      }
      throw new Error(message);
    }
    if (!result.cardToken) {
      throw new Error("No card token received from server")
    }
    return {
      success: true,
      cardToken: result.cardToken,
    }
  } catch (error) {
    console.error("Card tokenization error:", error)
    return {
      success: false,
      error: error.message,
    }
  }
}

let handlePayment = async () => {
  errorsContainer.value.textContent = '';
  successContainer.value.textContent = '';
  if (customerStore.meta.valid && customerStore.amount) {
    submitBtn.value.setAttribute('data-processing', 'data-processing');
    submitBtn.value.setAttribute('disabled', 'disabled');
    // STEP 1: Tokenize
    const tokenResult = await tokenizeCreditCard();
    console.log("Tokenization result:", tokenResult);
    if (!tokenResult.success || !tokenResult.cardToken) {
      let errorMessage = tokenResult?.error || "Failed to tokenize credit card";
      errorsContainer.value.textContent = errorMessage;
      submitBtn.value.removeAttribute('data-processing', 'data-processing');
      submitBtn.value.removeAttribute('disabled', 'disabled');
      alert(errorMessage);
    } else {
      // STEP 2: Payment request
      handleSumbit(tokenResult.cardToken);
    }
  }
}

const apiClient = inject('apiClient');
let handleSumbit = (tokenizedCreditCard) => {
  errorsContainer.value.textContent = '';
  successContainer.value.textContent = '';

  const response = apiClient
    .post('pay', {
      firstname: customerStore.firstName,
      lastname: customerStore.lastName,
      email: customerStore.email,
      amount: customerStore.amount,
      address1: customerStore.address,
      city: customerStore.city,
      state: customerStore.state,
      zip: customerStore.zip,
      merchant_id: merchant?.id,
      payment_token: tokenizedCreditCard,
      message: '' // honeypot ^^
    })
    .then(response => {
      if (response.status >= 200 && response.status < 300) {
        if (response?.data?.state == 'success') {
          errorsContainer.value.textContent = '';
          successContainer.value.textContent = response.data.message;

          // Clear payment inputs after success payment
          customerStore.setFieldValue('amount', undefined);
          customerStore.setFieldValue('ccnumber', '');
          customerStore.setFieldValue('ccexp', '');
          customerStore.setFieldValue('ccv', '');
          customerStore.validate();
        } else {
          errorsContainer.value.textContent = response.data.message;
          successContainer.value.textContent = '';
        }
      } else if (response?.reponse?.data) {
        throw new Error(response?.reponse?.data);
      } else {

      }
    })
    .catch(response => {
      successContainer.value.textContent = '';
      if (response?.data?.message) {
        errorsContainer.value.textContent = response?.data?.message || 'Unexpected network error. Try again later.';
      } else if (response?.response?.data?.message) {
        errorsContainer.value.textContent = response?.response?.data?.message || 'Unexpected network error. Try again later.';
      } else {
        errorsContainer.value.textContent = 'Unexpected error. Contact system administrator.';
      }
      console.log(response);
    })
    .finally(() => {
      submitBtn.value.removeAttribute('data-processing');
      submitBtn.value.removeAttribute('disabled');
    })
}

onMounted(() => {
  checkoutBodyElement.value.setAttribute('data-loading', true)
  const response = apiClient
    .post('create-page-token', { merchant_id: merchant?.id, message: '' })
    .then(response => {
      if (response.status >= 200 && response.status < 300) {
        errorsContainer.value.textContent = '';
        successContainer.value.textContent = '';
        console.log('Valor Pay: Client Token Response: ', response);
        if (!response?.data?.body?.clientToken) {
          throw new Error(response?.data);
        } else {
          customerStore.clientToken = response?.data?.body?.clientToken;
          timerRef.value.start();
        }
      } else {
        throw new Error(response?.data?.message || 'Unknown error (client token)');
      }
    })
    .catch(response => {
      console.log(response);
      let message = response?.response?.data.message || response?.data?.message || response?.message;
      message += '. Click Ok and page will be reloaded';
      if ( confirm(message) ) {
        window.location.reload(true);
      }
    })
    .finally(() => {
      checkoutBodyElement.value.removeAttribute('data-loading')
    })
});
</script>

<template>
  <div class="checkout">

    <div class="checkout__header" v-if="merchant?.logo || merchant?.name">
      <img v-if="merchant?.logo" :src="merchant?.logo" class="merchant-logo" />
      <div v-if="merchant?.name" v-text="merchant?.name" class="merchant-name"></div>
      <CountdownTimer ref="timerRef" :reloadOnExpire="true" />
    </div>

    <div class="checkout__body" ref="checkoutBodyElement">
      <div class="form-row form-row--mobile">
        <div class="text-field">
          <input name="firstName" type="text" placeholder="" v-model="customerStore.firstName"
            v-bind="customerStore.firstNameProps" />
          <label class="fieldLabel" for="firstName">First Name</label>
          <span v-show="customerStore.errors.firstName" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.firstName }}
          </span>
        </div>
        <div class="text-field">
          <input name="lastName" type="text" placeholder="" v-model="customerStore.lastName"
            v-bind="customerStore.lastNameProps" />
          <label class="fieldLabel" for="lastName">Last Name</label>
          <span v-show="customerStore.errors.lastName" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.lastName }}
          </span>
        </div>
      </div>

      <div class="text-field" :class="{ 'text-field--error': customerStore.errors.email }">
        <input name="email" type="text" placeholder="" v-model="customerStore.email"
          v-bind="customerStore.emailProps" />
        <label class="fieldLabel" for="email">Email</label>
        <span v-show="customerStore.errors.email" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.email }}
        </span>
      </div>

      <div class="text-field">
        <input name="address" type="text" placeholder="" v-model="customerStore.address"
          v-bind="customerStore.addressProps" />
        <label class="fieldLabel" for="address">Address</label>
        <span v-show="customerStore.errors.address" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.address }}
        </span>
      </div>

      <div class="form-row form-row--mobile">
        <div class="text-field">
          <input name="city" type="text" placeholder="" v-model="customerStore.city" v-bind="customerStore.cityProps" />
          <label class="fieldLabel" for="city">City</label>
          <span v-show="customerStore.errors.city" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.city }}
          </span>
        </div>

        <div class="select-field">
          <VueSelect v-model="customerStore.state" :options="statesOptions" placeholder="State" :is-searchable="false"
            :is-clearable="true" />
          <span v-show="customerStore.errors.state" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.state }}
          </span>
        </div>

      </div>


      <div class="text-field">
        <input name="zip" type="text" placeholder="" v-model="customerStore.zip" v-bind="customerStore.zipProps" />
        <label class="fieldLabel" for="zip">Postal code</label>
        <span v-show="customerStore.errors.zip" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.zip }}
        </span>
      </div>

      <div class="text-field">
        <input name="amount" type="text" placeholder="" v-model="customerStore.amount"
          v-bind="customerStore.amountProps" />
        <label class="fieldLabel" for="amount">Amount</label>
        <span v-show="customerStore.errors.amount" class="field-error" role="alert">
          <div class="icon" v-html="SvgNote"></div>
          {{ customerStore.errors.amount }}
        </span>
      </div>

      <!-- WadeWilson.js -->
      <div class="checkout__card-fields">
        <div class="text-field text-field--icon" ref="ccnField">
          <div class="icon" v-html="currentCardTypeSVG"></div>
          <input name="ccnumber" type="text" v-model="customerStore.ccnumber" v-bind="customerStore.ccnumberProps"
            v-maska data-maska="#### #### #### #### ###" placeholder="0000 0000 0000 0000" autocomplete="cc-number" />
          <label class="fieldLabel" for="ccnumber">Card Number</label>
          <span v-show="customerStore.errors.ccnumber" class="field-error" role="alert">
            <div class="icon" v-html="SvgNote"></div>
            {{ customerStore.errors.ccnumber }}
          </span>
        </div>
        <div class="form-row">
          <div class="text-field" ref="cceField">
            <input name="ccexp" type="text" placeholder="MM / YY" v-model="customerStore.ccexp"
              v-bind="customerStore.ccexpProps" v-maska="{ mask: '## / ##' }" autocomplete="cc-exp" />
            <label class="fieldLabel" for="ccexp">Exp. date</label>
            <span v-show="customerStore.errors.ccexp" class="field-error" role="alert">
              <div class="icon" v-html="SvgNote"></div>
              {{ customerStore.errors.ccexp }}
            </span>
          </div>
          <div class="text-field" ref="ccvField">
            <input name="cvv" type="text" placeholder="123" v-model="customerStore.cvv" v-bind="customerStore.cvvProps"
              v-maska="{ mask: cvvMask }" autocomplete="cvv" />
            <label class="fieldLabel" for="cvv">CVV</label>
            <span v-show="customerStore.errors.cvv" class="field-error" role="alert">
              <div class="icon" v-html="SvgNote"></div>
              {{ customerStore.errors.cvv }}
            </span>
          </div>
        </div>
      </div>

      <button type="button" id="payment-btn" class="submit-btn" ref="submit-btn" @click="handlePayment"
        :disabled="!customerStore.meta.valid || !customerStore.amount || customerStore.amount <= 0">
        {{ btnText }}
      </button>
    </div>


    <div class="message-container errors-container" ref="errors-container"></div>
    <div class="message-container success-container" ref="success-container"></div>

  </div>
</template>

<style lang="scss">
.message-container {
  box-sizing: border-box;
  width: 100%;
  border-radius: 0.25rem;
  max-height: 912px;
  border-width: 2px;
  border-style: solid;
  transition: max-height 0.2s 0s linear;
  padding: 1rem 1.25rem;
  margin-left: auto;
  margin-right: auto;
  margin-top: 1.75rem;

  &.errors-container {
    border-color: rgba(var(--error-color-rgb), 0.95);
  }

  &.success-container {
    border-color: rgba(var(--success-color-rgb), 0.95);
  }

  &:empty {
    overflow: hidden;
    padding: 0;
    max-height: 0;
    border-color: transparent !important;
    border-width: 0 !important;
    margin-top: 0 !important;
  }
}

#payment-btn {
  height: 56px;
  width: 100%;
  font-weight: 600;
}

.checkout {
  width: 100%;
  max-width: 640px;
  padding-left: 1rem;
  padding-right: 1rem;
  margin: 1rem auto 1.5rem;

  @media (min-width: 576px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    margin: 1.5rem auto;
  }

  &__header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-weight: 500;

    &:not(:empty) {
      margin-bottom: 1.875rem;
    }

    .merchant-name {
      font-size: 0.875rem;

      @media (min-width: 576px) {
        font-size: 1rem;
      }
    }

    img {
      height: auto;
      margin-right: 1.5rem;
      max-width: 120px;
      width: auto;
    }

    .cdt {
      margin-left: auto;
    }
  }

  &__body {
    /* LOADER SECTION */
    overflow: hidden;
    padding-top: 3px;
    padding-bottom: 3px;
    position: relative;

    &::before,
    &::after {
      content: '';
      position: absolute;
      display: block;
      text-align: center;
      z-index: -1;
      opacity: 0;
      transform-origin: 50% 50%;
    }

    &::after {
      left: 50%;
      top: 50%;
      transform: translate3d(-50%, -50%, 0);
    }

    &::before {
      background-color: var(--bg-color);
      width: 100%;
      height: 100%;
      left: 0;
      bottom: 0;
    }

    &::after {
      animation: spinner__animation 1s linear infinite;
      backface-visibility: hidden;
      border: .3em solid var(--form-spinner-color);
      border-left: .3em solid transparent;
      border-radius: 50%;
      margin: -.5em 0 0 -.5em;
      transform: translateZ(0) scale(.5);
      width: 3em;
      height: 3em;
    }

    &[data-loading] {
      max-height: 50vh;
      transition: max-height 0.15s 0s linear;

      &::before,
      &::after {
        opacity: 1;
        transition: opacity 0.2s 0s linear;
      }

      &::before {
        z-index: 99;
      }

      &::after {
        z-index: 100;
      }
    }

    /* LOADER SECTION */
  }

  &__card-fields {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    max-height: 1000px;
    position: relative;
    transition: max-height 0.15s 0s linear;
    margin-bottom: 1.875rem;
  }
}

.form-row {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: nowrap;

  .text-field,
  .select-field {
    flex: 0 0 calc(50% - 0.75rem);
  }

  &--mobile {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    .text-field,
    .select-field {
      flex: 0 0 100%;
    }

    @media (min-width: 576px) {
      justify-content: space-between;
      flex-direction: row;
      flex-wrap: nowrap;

      .text-field,
      .select-field {
        flex: 0 0 calc(50% - 0.75rem);
      }
    }
  }
}

.text-field {
  position: relative;
  width: 100%;
  flex: 1 1 100%;
  margin-bottom: 1.875rem;

  label {
    color: var(--input-color);
    line-height: 1;
    font-size: 16px;
    left: 1px;
    padding: 0;
    position: absolute;
    top: 1px;
    transition: font-size .2s ease, margin .15s ease-in-out;
    user-select: none;
    pointer-events: none;
    // margin: 17px 8px 8px;
    margin: 22px 16px 8px;
  }

  input {
    width: 100%;
    position: relative;
    box-shadow: none;
    overflow-y: visible;
    font-size: 1rem;
    line-height: 20px;
    border-radius: 4px;
    margin-bottom: 0;
    margin-top: 0;
    width: inherit;
    background: #fff;
    border: 2px solid var(--border-color);
    outline: none;
    padding: 26px 0.5rem 6px;
    color: var(--input-color);
    height: 56px; // workaround for firefox bug: https://hg.mozilla.org/mozilla-central/rev/b97aef275b5e

    &:hover {
      border-color: var(--input-color);

      &+label {
        color: var(--input-color);
      }
    }

    &:not(:focus)::placeholder,
    &:not(:active)::placeholder {
      color: transparent;
    }

    &:focus,
    &:active {

      &:not([disabled]):not(.text-field-error) {
        border-color: #333;
        outline: 3px solid rgba(var(--success-color-rgb), 0.3);
        background-color: rgba(var(--success-color-rgb), 0.1);
        color: #333;

        &:not([readonly])~label {
          margin-top: 0.5rem;
          margin-left: 0.5rem;
          font-size: 0.75rem;
        }
      }

    }

    &:not(:placeholder-shown):not([value=""])~label {
      margin-top: 0.5rem;
      margin-left: 0.5rem;
      font-size: 0.75rem;
    }

    &::-webkit-input-placeholder,
    &::-moz-placeholder {
      color: (0, 0, 0, 0.5);
      font-size: 0.875rem;
    }
  }

  &--frame>label {
    margin-top: 0.5rem;
    margin-left: 0.5rem;
    font-size: 0.75rem;
  }

  &--icon {
    &>.icon {
      height: 2rem;
      width: 2rem;
      margin-right: 0.125rem;
      margin-top: -0.25rem;
      display: flex;
      position: absolute;
      right: 0.5rem;
      top: 1rem;
      z-index: 1;
      color: #5a5a5a;

      svg {
        vertical-align: middle;
      }
    }
  }

  &--error {
    input {
      border-color: rgba(var(--error-color-rgb), 0.67);
      background-color: rgba(var(--error-color-rgb), 0.1);
    }
  }

  .field-error {
    // font-size: 0.75rem;
    font-size: 0.625rem;
    color: var(--error-color);
    line-height: 0.75rem;
    top: 100%;
    left: 0;
    position: absolute;
    letter-spacing: -0.25px;

    &:not(:empty) {
      // margin-top: 5px;
      margin-top: 2px;
    }

    .icon {
      height: 0.75rem;
      width: 0.75rem;
      margin-right: 0.125rem;
      margin-top: -0.25rem;
      display: inline-block;

      svg {
        vertical-align: middle;
      }
    }
  }
}

.select-field {
  position: relative;
  width: 100%;
  flex: 1 1 100%;
  margin-bottom: 1.875rem;

  .vue-select {

    // vue-select field settings
    --vs-border: 2px solid var(--border-color);
    --vs-text-color: var(--input-color);
    --vs-option-text-color: var(--input-color);
    --vs-input-outline: var(--border-color);
    --vs-option-focused-color: rgba(var(--success-color-rgb), 0.005);
    --vs-option-hover-color: rgba(var(--success-color-rgb), 0.2) --vs-option-selected-color: rgba(var(--success-color-rgb), 0.3);
    --vs-padding: 0.25rem 0.5rem 0.25rem 1rem;
    --vs-input-placeholder-color: var(--input-color);

    .menu-option.selected {
      background-color: rgba(var(--success-color-rgb), 0.3);
    }

    .control {
      height: 56px;

      &.focused {
        box-shadow: none !important;
        border-color: #5a5a5a;
        outline: 3px solid rgba(var(--success-color-rgb), 0.3);
        background-color: rgba(var(--success-color-rgb), 0.1)
      }
    }

    .clear-button,
    .dropdown-icon {
      display: inline-flex;
      align-content: center;
      align-items: center;
      justify-content: center;
    }

    .search-input {
      &::placeholder {
        opacity: 1;
      }
    }
  }

  .field-error {
    // font-size: 0.75rem;
    font-size: 0.625rem;
    color: var(--error-color);
    line-height: 0.75rem;
    top: 100%;
    left: 0;
    position: absolute;
    letter-spacing: -0.25px;

    &:not(:empty) {
      // margin-top: 5px;
      margin-top: 2px;
    }

    .icon {
      height: 0.75rem;
      width: 0.75rem;
      margin-right: 0.125rem;
      margin-top: -0.25rem;
      display: inline-block;

      svg {
        vertical-align: middle;
      }
    }
  }
}
</style>